import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Camera, CheckCircle2, XCircle, TrendingUp, Zap, ArrowRight, Plug, Check,
  Instagram, Facebook, Linkedin, Blocks,
} from "lucide-react";
import { toast } from "sonner";
import TopBar from "../components/layout/TopBar";
import useApi from "../hooks/useApi";

const AVAILABLE = [
  { key: "instagram", name: "Instagram", desc: "First-party post & Story analytics.", icon: Instagram },
  { key: "facebook", name: "Facebook Pages", desc: "Reach, engagement and page insights.", icon: Facebook },
  { key: "linkedin", name: "LinkedIn", desc: "Company page performance.", icon: Linkedin },
];

export default function Integrations() {
  const navigate = useNavigate();
  const niche = useApi("/nichecom").data;
  const listings = (niche?.properties || []).slice(0, 8);

  return (
    <>
      <TopBar title="Integrations" subtitle="Connect your data sources" />
      <div className="p-6 sm:p-8 max-w-[1400px] space-y-10">

        {/* Connected + available integrations */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Blocks size={18} strokeWidth={1.75} color="#5F8213" />
            <h2 className="text-xl font-bold tracking-tight text-ink">Connected sources</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* NicheCom — connected */}
            <motion.button
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              onClick={() => document.getElementById("nichecom-overview")?.scrollIntoView({ behavior: "smooth" })}
              className="card card-hover p-5 text-left relative overflow-hidden" data-testid="integration-nichecom">
              <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide" style={{ color: "#5F8213", background: "rgba(147,192,32,.15)" }}>
                <Check size={12} strokeWidth={3} /> Connected
              </span>
              <span className="grid place-items-center w-11 h-11 rounded-xl" style={{ background: "var(--grad-green-soft)" }}>
                <Camera size={20} strokeWidth={1.75} color="#5F8213" />
              </span>
              <h3 className="font-bold tracking-tight text-ink mt-4">NicheCom</h3>
              <p className="text-sm text-ink-2 mt-1 leading-relaxed">Media & content feed — properties, assets and utilisation.</p>
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold mt-3" style={{ color: "#5F8213" }}>View overview <ArrowRight size={14} strokeWidth={2} /></span>
            </motion.button>

            {AVAILABLE.map((a, i) => (
              <motion.div key={a.key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: (i + 1) * 0.05 }}
                className="card p-5 flex flex-col" data-testid={`integration-${a.key}`}>
                <span className="grid place-items-center w-11 h-11 rounded-xl border" style={{ borderColor: "#E7E8E4" }}>
                  <a.icon size={20} strokeWidth={1.5} className="text-ink-2" />
                </span>
                <h3 className="font-bold tracking-tight text-ink mt-4">{a.name}</h3>
                <p className="text-sm text-ink-2 mt-1 leading-relaxed flex-1">{a.desc}</p>
                <button onClick={() => toast.info(`${a.name} connection coming soon`)} data-testid={`connect-${a.key}`}
                  className="btn-secondary focus-ring mt-3 w-full justify-center"><Plug size={14} strokeWidth={1.75} /> Connect</button>
              </motion.div>
            ))}
          </div>
        </section>

        {/* NicheCom overview */}
        <section id="nichecom-overview">
          <div className="flex items-baseline gap-3 mb-4">
            <h2 className="text-xl font-bold tracking-tight text-ink">NicheCom overview</h2>
            <span className="text-sm text-ink-2">{niche?.month} · {niche?.photographed} properties photographed</span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { l: "Properties photographed", v: niche?.photographed, icon: Camera, c: "#151515" },
              { l: "Used on social", v: niche?.used, icon: CheckCircle2, c: "#678A16" },
              { l: "Not yet used", v: niche?.not_used, icon: XCircle, c: "#D95C5C" },
              { l: "Content utilisation", v: niche ? `${niche.utilisation}%` : "", icon: TrendingUp, c: "#93C020" },
            ].map((s, i) => (
              <motion.div key={s.l} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} className="card p-5" data-testid={`nichecom-stat-${i}`}>
                <s.icon size={18} strokeWidth={1.5} style={{ color: s.c }} />
                <div className="text-4xl font-extrabold tracking-tighter text-ink mt-3">{s.v}</div>
                <div className="text-sm text-ink-2 mt-1">{s.l}</div>
              </motion.div>
            ))}
          </div>

          {/* Recent listings — instantly available */}
          <div className="card mt-4 overflow-hidden" data-testid="nichecom-recent-listings">
            <div className="px-5 py-3.5 border-b flex items-center justify-between" style={{ borderColor: "#E7E8E4" }}>
              <span className="label-caps">Recent listings</span>
              <button className="btn-primary text-sm focus-ring" onClick={() => navigate("/app/content")} data-testid="turn-unused"><Zap size={14} strokeWidth={2} /> Turn unused content into posts</button>
            </div>
            <div className="divide-y" style={{ borderColor: "#E7E8E4" }}>
              <div className="hidden sm:grid grid-cols-12 px-5 py-2 label-caps">
                <span className="col-span-5">Property</span><span className="col-span-4">Content available</span><span className="col-span-2">Shoot date</span><span className="col-span-1 text-right">Status</span>
              </div>
              {listings.map((p) => (
                <div key={p.id} className="grid grid-cols-12 items-center px-5 py-3 gap-2" data-testid={`listing-${p.id}`}>
                  <div className="col-span-12 sm:col-span-5 flex items-center gap-3">
                    <img src={p.image} alt="" className="w-10 h-10 rounded-md object-cover" />
                    <div><div className="font-semibold text-ink text-sm">{p.address}</div><div className="text-xs text-ink-3">{p.type} · {p.price}</div></div>
                  </div>
                  <div className="col-span-8 sm:col-span-4 flex flex-wrap gap-1.5">
                    {p.media.map((m) => <span key={m} className="text-xs font-medium text-ink-2 rounded-full border px-2 py-0.5" style={{ borderColor: "#E7E8E4" }}>{m}</span>)}
                  </div>
                  <div className="col-span-2 hidden sm:block text-xs text-ink-2">{p.shoot_date}</div>
                  <div className="col-span-4 sm:col-span-1 text-right">
                    {p.posted
                      ? <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5" style={{ background: "rgba(147,192,32,.15)", color: "#5F8213" }}>Posted</span>
                      : <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5" style={{ background: "rgba(217,92,92,.1)", color: "#B23B3B" }}>Not posted</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
